import { MediaAssetProps } from "design-system/components/primitives/media-asset/media-asset";
import { MediaAssetImageProps } from "design-system/components/primitives/media-asset/media-asset-image";

import { isContentType } from "frontend/contentful/lib/is-content-type";
import {
  ImageEntry,
  OrganizationEntry,
  PersonEntry,
  PersonWrapperEntry,
  VideoEntry,
  WowzaEmbedEntry,
} from "frontend/contentful/schema/semantics";
import { useContentful } from "frontend/hooks/use-contentful";
import { createContentfulProps } from "../../lib/create-contentful-props";
import { getEnterpriseIdHeadshotSrc } from "../utils/enterprise-id";
import { RenderContentfulRichText } from "../utils/render-contentful-rich-text";

type MediaEntry = ImageEntry | VideoEntry;

export const createMediaAssetProps = createContentfulProps<
  MediaEntry,
  MediaAssetProps
>(({ entry, createEditAttributes }) => {
  const { getAsset, getEntry } = useContentful();

  const asset = getAsset(entry.fields.asset);

  if (isContentType<ImageEntry>("image", entry)) {
    if (asset?.fields?.file?.url) {
      const mediaAssetImageProps: MediaAssetImageProps = {
        src: asset?.fields?.file?.url,
        alt:
          entry.fields.altText?.toLowerCase() === "na"
            ? undefined
            : entry.fields.altText,
        editAttributes: {
          src: createEditAttributes({ entry, fieldId: "asset" }),
        },
      };

      if (entry.fields.caption && entry.fields.showCaption) {
        mediaAssetImageProps.caption = (
          <RenderContentfulRichText document={entry.fields.caption} />
        );
      }

      if (entry.fields.imageGravity) {
        mediaAssetImageProps.gravity = entry.fields.imageGravity;
      }

      if (asset?.fields?.file?.details?.image?.width) {
        mediaAssetImageProps.width =
          asset.fields.file.details.image.width.toString();
      }

      if (asset?.fields?.file?.details?.image?.height) {
        mediaAssetImageProps.height =
          asset.fields.file.details.image.height.toString();
      }
      if (
        asset?.fields?.file?.details?.image?.width &&
        asset?.fields?.file?.details?.image?.height
      ) {
        mediaAssetImageProps.intrinsicAspectRatio = `${asset.fields.file.details.image.width}/${asset.fields.file.details.image.height}`;
      }
      return {
        image: mediaAssetImageProps,
      };
    }
  }

  if (isContentType<WowzaEmbedEntry>("wowzaEmbed", entry)) {
    const mediaEntry = getEntry(entry.fields.media);
    const imageEntry = mediaEntry?.fields.asset;
    const imageUrl = getAsset(imageEntry)?.fields?.file?.url;
    const caption = mediaEntry?.fields.caption;

    return {
      wowzaEmbed: {
        videoPath: entry.fields.videoPath ?? "",
        caption: caption ? (
          <RenderContentfulRichText document={caption} />
        ) : undefined,
        media: {
          src: imageUrl ?? "",
        },
      },
    };
  }

  if (isContentType<VideoEntry>("video", entry)) {
    if (entry.fields.externalURL) {
      return {
        embed: {
          title: entry.fields.title || "",
          src: entry.fields.externalURL,
          ...(entry.fields.description && {
            descriptionVideo: (
              <RenderContentfulRichText document={entry.fields.description} />
            ),
          }),
          descriptionImg: asset?.fields?.description,
          width: 16,
          height: 9,
        },
      };
    }

    if (entry.fields.kalturaId) {
      return {
        kaltura: {
          entryId: entry.fields.kalturaId,
          threePlayId: entry.fields.threePlayID,
          threePlayProjectId: entry.fields.threePlayProjectID,
          title: entry.fields.title,
          duration: entry.fields.duration,
          ...(entry.fields.description && {
            description: (
              <RenderContentfulRichText document={entry.fields.description} />
            ),
          }),
          caption: entry.fields.caption ? (
            <RenderContentfulRichText document={entry.fields.caption} />
          ) : entry.fields.kalturaCaption ? (
            <RenderContentfulRichText document={entry.fields.kalturaCaption} />
          ) : undefined,
          hide: { caption: entry.fields.hideCaption ?? true },
        },
      };
    }

    if (asset?.fields?.file?.url && asset.fields.file.contentType) {
      const imageEntry = getEntry(entry.fields.backupImage)?.fields.asset;
      const backupImage = getAsset(imageEntry)?.fields?.file?.url;
      return {
        video: {
          src: asset.fields.file.url,
          backupImage: backupImage,
          mimeType: asset.fields.file.contentType,
          ...(entry.fields.description && {
            descriptionVideo: (
              <RenderContentfulRichText document={entry.fields.description} />
            ),
          }),
          descriptionImg: asset?.fields?.description,
          caption: entry.fields.caption ? (
            <RenderContentfulRichText document={entry.fields.caption} />
          ) : undefined,
          hide: { caption: entry.fields.hideCaption ?? true },
          editAttributes: {
            src: createEditAttributes({ entry, fieldId: "asset" }),
          },
        },
      };
    }
  }
});

/**
 * Includes align and caption - for embeded media within Rich Text
 */
export const createRichMediaAssetProps = createContentfulProps<
  MediaEntry,
  MediaAssetProps
>(({ entry }) => {
  const props = createMediaAssetProps(entry);

  if (isContentType<ImageEntry>("image", entry)) {
    if (props?.image) {
      if (entry.fields.caption) {
        props.image.caption = (
          <RenderContentfulRichText document={entry.fields.caption} />
        );
      }

      if (entry.fields.alignment) {
        props.image.align = `align-${entry.fields.alignment}`;

        if (
          entry.fields.alignment === "left" ||
          entry.fields.alignment === "right"
        ) {
          props.image.mobileWidth = 300;
        }
      }
    }
  }

  if (isContentType<VideoEntry>("video", entry)) {
    if (props?.video) {
      props.video.align = "align-center";
    } else if (props?.embed) {
      props.embed.align = "center";
    }
  }

  return props;
});

export const createPersonImageProps = createContentfulProps<
  PersonEntry | PersonWrapperEntry | OrganizationEntry,
  MediaAssetProps
>(({ entry }) => {
  const { getIsPersonPastHbsFaculty, getEntry, getEntityName } =
    useContentful();

  const getPersonEntry = (
    entry: PersonEntry | PersonWrapperEntry | OrganizationEntry,
  ): PersonEntry | undefined => {
    if (isContentType<PersonWrapperEntry>("personWrapper", entry)) {
      const person = getEntry(entry.fields.person);
      if (!person) return undefined;
      return getPersonEntry(person);
    }

    if (isContentType<OrganizationEntry>("organization", entry)) {
      return undefined;
    }

    if (getIsPersonPastHbsFaculty(entry)) {
      return undefined;
    }

    return entry;
  };

  if (isContentType<PersonWrapperEntry>("personWrapper", entry)) {
    if (entry.fields.image) {
      return createMediaAssetProps(entry.fields.image);
    }
  }

  const personEntry: PersonEntry | undefined = getPersonEntry(entry);
  if (!personEntry) return undefined;

  const { image, hbsEnterpriseID } = personEntry.fields;

  if (image) {
    return createMediaAssetProps(image);
  }

  if (hbsEnterpriseID) {
    const personName = getEntityName(personEntry);
    const mediaAssetImageProps: MediaAssetImageProps = {
      src: getEnterpriseIdHeadshotSrc(personEntry),
      alt: `Headshot of ${personName}`,
    };
    return {
      image: mediaAssetImageProps,
    };
  }
});
